import { useTranslation } from 'react-i18next'

import {
  Box,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
} from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import { TABLE_HEADER_CELL_STYLE } from '~/components/common/MasterDataTable'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { convertAreaLabel } from '~/models/area'
import {
  KOMA_DIVIDER_ROW_CSS,
  komaListDividerFlags,
} from '~/models/komaDivider'
import { formatKomaLabel } from '~/models/komaLabel'
import type { BulkPreviewRow } from '~/pages/SpotBidding/models/bulkCreate'
import { spotFeeAdjustmentViolationLines } from '~/pages/SpotBidding/models/feePreview'

type Props = {
  /** 生成札 + 行単位の入札制限判定 (= 表示する行そのもの)。 */
  rows: readonly BulkPreviewRow[]
  /** JEPX自由記述欄。 生成札全件へ同一適用されるので行ごとの値は持たない。 */
  memoJepx?: string
}

/**
 * 一括作成 Step2 (内容確認) の生成札一覧
 * (design `spot.html` `#bblk-step2-body`、 spec spot-bidding.md AC-BSP-005)。
 *
 * 生成される各札を 1 行として、 時間帯・エリア・売/買・価格・数量・入札制限・
 * JEPX自由記述欄 の 7 列で表示する。 判定は行に同梱された `status` を描くだけで、
 * ここでは判定しない (= Step1 の集約警告と結論が食い違わないよう evaluator を
 * `buildBulkPreviewRows` 1 本に固定しているため)。
 */
export const BulkPreviewTable = ({
  rows,
  memoJepx,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // 3 時間 (6 コマ) ごとの区切り線 (conventions.md §48 コマの表の区切り線)。 コマが飛ぶ・
  // 1 コマが複数行の一覧なので、 境目をまたぐ行の下に 1 本だけ引く。
  // エリアごとに並ぶので、 エリアが変わる所には引かない。
  const komaDividerFlags = komaListDividerFlags(
    rows,
    (bid) => bid.timeCode,
    (bid) => String(bid.areaCode),
  )
  return (
    <Box overflowY="auto" maxHeight="400px">
      <TableRoot variant="line">
        <TableHeader
          position="sticky"
          zIndex={1}
          top={0}
          // TH は同じ画面の他の表と同じ éclair の規定 (地 Surface.tableHeader /
          // Text.midEmphasis / 600 / 10px 12px。 UI 辞書 table.md §0)。
          // `textAlign` は列ごとに違うので css には入れない (各 TH の prop が正)。
          css={{ '& th': TABLE_HEADER_CELL_STYLE }}
        >
          <TableRow>
            <TableColumnHeader>
              {t('page:biddingSpot.bulkCreate.columnTime')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.bulkCreate.columnArea')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.bulkCreate.columnSide')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingSpot.bulkCreate.columnPrice')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingSpot.bulkCreate.columnVolume')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.bulkCreate.columnLimit')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingSpot.bulkCreate.columnMemoJepx')}
            </TableColumnHeader>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((bid, i) => {
            const status = bid.status
            const key = `${bid.areaCode}-${bid.timeCode}-${bid.direction}-${i}`
            return (
              <TableRow
                key={key}
                css={
                  komaDividerFlags[i] === true
                    ? KOMA_DIVIDER_ROW_CSS
                    : undefined
                }
              >
                <TableCell whiteSpace="nowrap">
                  {formatKomaLabel(t, bid.timeCode)}
                </TableCell>
                <TableCell textAlign="center">
                  {convertAreaLabel(bid.areaCode)}
                </TableCell>
                <TableCell textAlign="center">
                  <TradeSideLabel side={bid.direction} />
                </TableCell>
                <TableCell
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {Number(bid.priceYenPerKwh).toFixed(2)}
                  {bid.feeAdjustmentViolation !== undefined ? (
                    <Text
                      display="block"
                      color="Text.negativeHigh"
                      textAlign="end"
                    >
                      <Text as="span" display="block" fontWeight="semibold">
                        {
                          spotFeeAdjustmentViolationLines(
                            t,
                            bid.feeAdjustmentViolation,
                          ).line1
                        }
                      </Text>
                      <Text as="span" display="block">
                        {
                          spotFeeAdjustmentViolationLines(
                            t,
                            bid.feeAdjustmentViolation,
                          ).line2
                        }
                      </Text>
                    </Text>
                  ) : null}
                </TableCell>
                {/* 数量はコマ別 MW をそのまま表示する (issue #4285 で保持・
                    書込 contract まで MW 建てに統一済み。 `volumeMwh` という
                    field 名は proto / DB 列名に合わせた歴史的経緯)。
                    上限数量(コマ計) 超過 (AC-BSP-061) 時は規定文言を該当項目 (数量)
                    直下にインライン表示する (spec spot-bidding.md §9)。 他の違反軸
                    (価格上下限・上限数量(1札)・階段数) はバッジのみで固定文言表示の
                    配線は未実装 (既存の badge 表示に留める、 本 PR のスコープ外)。 */}
                <TableCell
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {Number(bid.volumeMwh).toFixed(1)}
                  {bid.volumeTotalViolation !== undefined ? (
                    // ecloud <Text> は内部 textStyle=sm (12px 固定) で fontSize prop を
                    // 無視する (frontend-font-size-uniformity)。 本行は上の数量 (14px)
                    // より意図的に小さいキャプションなので、 その 12px 固定をそのまま使う。
                    <Text
                      display="block"
                      color="Text.warningHigh"
                      textAlign="end"
                    >
                      {t('page:biddingSpot.bulkCreate.volumeTotalErrorDetail', {
                        actual: bid.volumeTotalViolation.actual,
                        limit: bid.volumeTotalViolation.limit,
                      })}
                    </Text>
                  ) : null}
                </TableCell>
                {/* 範囲内 = positive / 判定できていない (未設定と同義の未確定) =
                    neutral / 違反 = warning。
                    違反は「人の対応を待つ」状態なので warning を採る (negative は
                    「失敗・差戻し・下限違反」で、上限超過はここに入らない)。
                    行バッジと同じ事実を指す上部 Alert も warning で
                    揃える。 design `_bblkRenderStep2` は badge-error だが、
                    design の negative 定義 (`spot.html:551`) は「**送信**を自動
                    抑止する違反」であり、送信を止めるのは承認申請 / 提出ゲートの側。
                    数量指定モード (AC-BSP-005c) の 上限数量(1札) 超過だけは確定も
                    止まるが (#8324)、その「作成できない」事実は行バッジではなく
                    上部の error Alert が担う (行バッジは軸を区別しないため)。 */}
                <TableCell textAlign="center">
                  {status === 'violation' ? (
                    <Badge status="warning">
                      {t('page:biddingSpot.bulkCreate.limitViolationBadge')}
                    </Badge>
                  ) : status === 'unknown' ? (
                    <Badge status="neutral">
                      {t('page:biddingSpot.bulkCreate.limitUnknown')}
                    </Badge>
                  ) : (
                    <Badge status="positive">
                      {t('page:biddingSpot.bulkCreate.limitOk')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell whiteSpace="pre-wrap">{memoJepx ?? ''}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </TableRoot>
    </Box>
  )
}
